import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Typography } from '@speeki/global-ui-components';
import { IconManager, IconName } from '@speeki/icons';
import { useOnClickOutside } from '@speeki/react-hooks';

import { ValueLabelProps } from '@@types/CommonTypes';

import { useUserModules } from '@utils/hooks/useUserModules';

import { getPath } from '@services/RouteService/paths';

import {
    ActionSelect,
    SelectItem,
    SelectLink,
    SelectWrapper,
} from './MakeActionSelect.styled';

interface MakeActionSelectProps {
    className?: string;
}

type MakeActionSelectType = 'cases' | 'surveys' | 'disclosures';

interface SelectAction extends ValueLabelProps<MakeActionSelectType> {
    icon: IconName;
    path: string;
}

const actions: SelectAction[] = [
    {
        icon: 'Svg2DMessaging24',
        label: 'MAKE_REPORT',
        path: getPath({ path: '/new-conversation' }),
        value: 'cases',
    },
    {
        icon: 'Svg2DDAndD24',
        label: 'MAKE_DECLARATION',
        path: getPath({ path: '/disclosure/list' }),
        value: 'disclosures',
    },
    {
        icon: 'Svg2DSurvey24',
        label: 'PARTICIPATE_IN_SURVEY',
        path: getPath({ path: '/survey/list' }),
        value: 'surveys',
    },
];

export const MakeActionSelect = memo(({ className }: MakeActionSelectProps) => {
    const selectRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();
    useOnClickOutside(selectRef, () => setIsOpen(false));

    const { data } = useUserModules();
    const modules = data?.user.tenant?.modules;
    const actionModules: Partial<Record<MakeActionSelectType, boolean>> = {
        ...modules,
    };

    return (
        <SelectWrapper className={className}>
            <Button
                disabled={isOpen}
                iconProps={{
                    icon: 'SvgPlus24',
                    size: 24,
                    themeColors: {
                        default: {
                            'color-1': 'secondary.default',
                        },
                    },
                }}
                onClick={() => setIsOpen(true)}
                testId="button_action-select"
                variant="primary"
            />
            {isOpen && (
                <ActionSelect ref={selectRef}>
                    {actions.map(
                        ({ icon, label, path, value }) =>
                            actionModules[value] && (
                                <SelectLink
                                    data-testid={`d_2oe3o-${value}`}
                                    key={value}
                                    to={path}
                                >
                                    <SelectItem>
                                        <IconManager icon={icon} size={24} />
                                        <Typography.TextBody_16_24>
                                            {t(`${label}`)}
                                        </Typography.TextBody_16_24>
                                    </SelectItem>
                                </SelectLink>
                            ),
                    )}
                </ActionSelect>
            )}
        </SelectWrapper>
    );
});
